import { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export function useHomeData() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/home`);
      if (!response.ok) throw new Error('Failed to fetch home data');
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useOfferings(tab = 'announcements') {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOfferings = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/offerings?tab=${tab}`);
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (err) {
        console.error('Error fetching offerings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOfferings();
  }, [tab]);

  return { data, loading };
}

export function useMenuItems() {
  // Default menu items initialized directly
  const items = [
    { id: 'home', label: 'HOME', url: '/' },
    { id: 'about', label: 'ABOUT US', url: '/about', hasSubmenu: true },
    { id: 'research', label: 'RESEARCH', url: '/research', hasSubmenu: true },
    { id: 'publications', label: 'PUBLICATIONS', url: '/publications', hasSubmenu: true },
    { id: 'technologies', label: 'TECHNOLOGIES', url: '/technologies', hasSubmenu: true },
    { id: 'varieties', label: 'VARIETIES', url: '/varieties', hasSubmenu: true },
    { id: 'farmers', label: 'FOR FARMERS', url: '/farmers', hasSubmenu: true },
    { id: 'nursery', label: 'NURSERY', url: '/nursery' },
    { id: 'media', label: 'MEDIA', url: '/media', hasSubmenu: true },
    { id: 'contact', label: 'CONTACT US', url: '/contact' }
  ];

  return items;
}

export function useUsefulLinks() {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/useful-links`);
        if (response.ok) {
          const result = await response.json();
          setLinks(result);
        } else {
          // Default links
          setLinks([
            { name: 'eHRMS 2.0', url: 'https://e-hrms.gov.in/login' },
            { name: 'DARE/ICAR e-Office', url: 'https://eoffice.icar.gov.in/SSOComponent/auth.php' },
            { name: 'DARE/ICAR Sparrow', url: 'https://sparrow.icar.gov.in/' },
            { name: 'NIC mail', url: 'https://mail.gov.in/' },
            { name: 'ICAR', url: 'https://icar.org.in/en' },
            { name: 'NHB', url: 'https://www.nhb.gov.in/' }
          ]);
        }
      } catch (err) {
        // Default links on error
        setLinks([
          { name: 'eHRMS 2.0', url: 'https://e-hrms.gov.in/login' },
          { name: 'DARE/ICAR e-Office', url: 'https://eoffice.icar.gov.in/SSOComponent/auth.php' },
          { name: 'DARE/ICAR Sparrow', url: 'https://sparrow.icar.gov.in/' },
          { name: 'NIC mail', url: 'https://mail.gov.in/' },
          { name: 'ICAR', url: 'https://icar.org.in/en' },
          { name: 'NHB', url: 'https://www.nhb.gov.in/' }
        ]);
      }
    };
    fetchLinks();
  }, []);

  return links;
}

export default useHomeData;
